<template>
    <div class="pa-4 fill-height d-flex flex-column flex-grow-1">
        <AgGrid class="flex-grow-1" ref="applicationDomainGridRef" :fetchUrl="applicationDomainFetchUrl"
        :columnDefs="applicationDomainColumnDefs" :gridParams="applicationDomainGridParams" :groupDefaultExpanded="groupDefaultExpanded"
        :refresh="applicationDomainRefresh" :domLayout="domLayout" />
    </div>

    <!-- Add Application Domain Dialog -->
    <v-dialog v-model="addApplicationDomainDialogVisible" class="w-33">
        <v-card>
            <v-card-title>{{ $t('dialog.newApplicationDomain') }}</v-card-title>

            <v-divider />

            <v-form @submit.prevent v-model="vformApplicationDomain">
                <v-card-text>
                    <v-container>
                        <v-text-field v-model="newApplicationDomain.name" :label="$t('field.label')" variant="outlined" required maxlength="100" counter="100" :rules="[rules.required]" />
                        <v-text-field v-model="newApplicationDomain.customerCode" :label="$t('field.customerName')" variant="outlined" required maxlength="50" counter="50" :rules="[rules.required]" />

                        <v-select v-model="newApplicationDomain.processId" :label="$t('field.type')" variant="outlined" required :rules="[rules.required]" :items="applicationDomainProcesses" item-title="name" item-value="id" />
                    </v-container>
                </v-card-text>

                <v-divider />

                <v-card-actions class="justify-end">
                    <v-btn variant="text" @click="addApplicationDomainDialogVisible = false">{{ $t('action.cancel') }}</v-btn>
                    <v-btn color="primary" variant="flat" @click="saveApplicationDomain" :disabled="!vformApplicationDomain">{{ $t('action.ok') }}</v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>
</template>

<script setup>
    import { computed, inject, onMounted, ref, watch, useTemplateRef } from 'vue';
    import AgGrid from '@/components/AgGrid.vue';
    import applicationDomainProcessJson from '@/components/json/applicationDomainProcess.json';
    import { toast } from 'vue3-toastify';
    import { useLogStore } from '@/stores/logStore';
    import { useUserStore } from '@/stores/userStore';

    const props = defineProps({
        customerReference: {
            type: String,
            default: null,
        },
    });
    const emit = defineEmits(['pending-changes']);

    const urlWebAPI = inject('urlWebAPI');
    const translate = inject('translate');
    const getFormBody = inject('getFormBody');

    const logStore = useLogStore();
    const userStore = useUserStore();

    const domLayout = 'normal';
    const applicationDomainProcesses = applicationDomainProcessJson;

    const applicationDomainGridRef = useTemplateRef('applicationDomainGridRef');
    const applicationDomainFetchUrl = 'GetApplicationDomains';
    const applicationDomainColumnDefs = ref([
        {
            field: 'processName',
            rowGroup: true,
            hide: true,
            lockVisible: true,
            
        },
        {
            field: 'applicationDomainName',
            headerName: translate('applicationDomain', 'column'),
            minWidth: 200,
            pinned: 'left',
            lockVisible: true,
            
        },
    ]);
    const applicationDomainGridParams = {
        token: userStore.getUser().token,
        customerReference: props.customerReference,
    };

    const applicationDomainRefresh = ref({
        state: false,
        type: 'column',
    });

    const groupDefaultExpanded = -1;

    const addApplicationDomainDialogVisible = ref(false);
    const vformApplicationDomain = ref(false);
    const newApplicationDomain = ref({
        name: '',
        customerCode: '',
        processId: '',
    });

    const rowsUpdated = ref([]);

    const rules = {
        required: v => (v !== null && v !== '' && typeof v !== 'undefined') || translate('required', 'toast'),
    };

    const hasPendingChanges = computed(() => rowsUpdated.value.length > 0);

    watch(hasPendingChanges, (value) => {
        emit('pending-changes', value);
    });

    onMounted(() => {
        getApplicationDomainColumns().then(() => {
            applicationDomainGridRef.value.getGridData().then(() =>{
                applicationDomainGridRef.value.gridRef.api.setGridOption("loading", false);
            })
        });
    });

    function getApplicationDomainColumns() {
        var formBody = getFormBody(applicationDomainGridParams);

        return fetch(urlWebAPI + 'GetModules', {
            method: 'POST',
            body: formBody,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            }
        })
            .then(result => result.json())
            .then(modules => {
                var parentModules = modules.filter(m => m.parentId == 0);

                for (var i = 0; i < parentModules.length; i++) {
                    var parentModule = parentModules[i];

                    applicationDomainColumnDefs.value.push({
                        field: parentModule.name,
                        headerName: parentModule.name,
                        headerClass: (i > 0) ? 'group-start' : '',
                        children: [],
                        
                    });

                    var childrenModules = modules.filter(m => m.parentId == parentModule.id);

                    for (var j = 0; j < childrenModules.length; j++) {
                        var childModule = childrenModules[j];

                        applicationDomainColumnDefs.value.last().children.push({
                            field: childModule.name,
                            headerName: childModule.name,
                            headerClass: (applicationDomainColumnDefs.value.last().children.length == 0 && i > 0) ? 'header-center group-start' : 'header-center',
                            cellClass: (applicationDomainColumnDefs.value.last().children.length == 0 && i > 0) ? 'header-center group-start' : 'header-center',
                            type: 'module',
                            minWidth: 120,
                             
                        });
                    }
                }
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetModules data.');
            });
    }

    function openAddDialog() {
        newApplicationDomain.value = {
            name: '',
            customerCode: '',
            processId: '',
        };

        addApplicationDomainDialogVisible.value = true;
    }

    function saveApplicationDomain() {
        var processSelected = applicationDomainProcesses.filter(adp => adp.id == parseInt(newApplicationDomain.value.processId)).first();

        var applicationDomain = {
            process: processSelected.id,
            type: processSelected.type,
            customerCode: newApplicationDomain.value.customerCode,
            applicationDomainName: newApplicationDomain.value.name,
        };

        const data = {
            token: userStore.getUser().token,
            customerReference: props.customerReference,
            applicationDomainModules: [applicationDomain],
        };

        $.postData(urlWebAPI + 'SaveApplicationDomains', data)
            .then(() => {
                addApplicationDomainDialogVisible.value = false;
                applicationDomainRefresh.value = { type: 'data', state: true };

                setTimeout(() => {
                    applicationDomainRefresh.value = { ...applicationDomainRefresh.value, state: false };
                }, 1000);
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing SaveApplicationDomains data.');
            });
    }

    function save() {
        let data = {
            token: userStore.getUser().token,
            customerReference: props.customerReference,
            applicationDomainModules: rowsUpdated.value,
        };

        $.postData(urlWebAPI + 'SaveApplicationDomains', data)
            .then(() => {
                rowsUpdated.value = [];
                toast.success(translate('customerParamsWellSaved', 'toast'));
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing SaveApplicationDomains data.');
            });
    }

    function undo() {
        var gridApi = applicationDomainGridRef.value.gridRef.api;

        for (var i = 0; i < gridApi.getCurrentUndoSize() + 1; i++)
            setTimeout(() => {
                gridApi.undoCellEditing();
            }, 10 * i);

        gridApi.redrawRows();
        rowsUpdated.value = [];
    }

    // ViewManageCustomer.vue calls undo()/save()/openAddDialog() through a template ref, and
    // AgGrid.vue reaches into rowsUpdated through the $parent chain — both only see what
    // defineExpose() exposes.
    defineExpose({ undo, save, openAddDialog, rowsUpdated });
</script>
<style >
body .group-start {
    border-left: 1px solid #e2e2e2;
}
</style>